/**
 * createWriteQueue
 *
 * Factory for write queue state management.
 * Provides promise chaining, blocked state detection, and deferred snapshot handling.
 * Used by both persistDocument and persistCollection.
 */

/**
 * Core write state fields.
 * Tracks pending updates, in-flight updates, and active write status.
 */
export type WriteQueueState<TDeferred> = {
  /** Data waiting to be written (merged from multiple rapid updates) */
  updatePending?: unknown;
  /** Data currently being written to Firestore */
  updateInFlight?: unknown;
  /** True while the actual Firestore write is in progress */
  writing: boolean;
  /** Promise chain for serializing write operations */
  writeChain?: Promise<void>;
  /** Snapshot deferred until writes complete */
  deferredSnapshot?: TDeferred;
};

/**
 * Configuration for write queue behavior.
 */
type WriteQueueOptions<TDeferred> = {
  /** Called when deferred snapshot should be applied */
  onApplyDeferred?: (deferred: TDeferred) => void;
  /** Called after chain completes (for cleanup) */
  onChainComplete?: () => void;
};

/**
 * Write queue interface.
 */
export type WriteQueue<TDeferred> = {
  /** The mutable state object */
  state: WriteQueueState<TDeferred>;
  /** Check if writes are blocked (pending/in-flight/writing) */
  isBlocked: () => boolean;
  /** Enqueue a write operation onto the promise chain */
  enqueue: (op: () => Promise<void>) => Promise<void>;
  /** Apply deferred snapshot if not blocked */
  applyDeferredIfAny: () => void;
  /** Set a deferred snapshot */
  setDeferred: (deferred: TDeferred) => void;
  /** Clear the deferred snapshot */
  clearDeferred: () => void;
  /** Get the current deferred snapshot */
  getDeferred: () => TDeferred | undefined;
};

/**
 * Creates a write queue for managing Firestore write operations.
 *
 * Features:
 * - Promise chaining to serialize writes
 * - Blocked state detection (pending/in-flight/writing)
 * - Deferred snapshot storage and application
 *
 * @param options - Configuration callbacks
 */
export function createWriteQueue<TDeferred>(
  options: WriteQueueOptions<TDeferred> = {}
): WriteQueue<TDeferred> {
  const state: WriteQueueState<TDeferred> = { writing: false };

  const isBlocked = (): boolean => {
    return !!state.updatePending || !!state.updateInFlight || state.writing;
  };

  const applyDeferredIfAny = (): void => {
    if (isBlocked()) return;

    const deferred = state.deferredSnapshot;
    if (!deferred) return;
    state.deferredSnapshot = undefined;

    options.onApplyDeferred?.(deferred);
  };

  const enqueue = (op: () => Promise<void>): Promise<void> => {
    const prev = state.writeChain ?? Promise.resolve();
    const operationPromise = prev.then(op);
    const chain = operationPromise.catch(() => {});
    state.writeChain = chain;

    void chain.finally(() => {
      if (state.writeChain !== chain) return;
      state.writeChain = undefined;
      applyDeferredIfAny();
      options.onChainComplete?.();
    });

    return operationPromise;
  };

  const setDeferred = (deferred: TDeferred): void => {
    state.deferredSnapshot = deferred;
  };

  const clearDeferred = (): void => {
    state.deferredSnapshot = undefined;
  };

  const getDeferred = (): TDeferred | undefined => {
    return state.deferredSnapshot;
  };

  return {
    state,
    isBlocked,
    enqueue,
    applyDeferredIfAny,
    setDeferred,
    clearDeferred,
    getDeferred,
  };
}
